import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { CoreProvider } from '../core.provider';
import { IntegrationCredentialModelDto, IntegrationTestResultDto, StreamListModelDto, StreamOptionModelDto } from '../api/models';

/** Providers the integrations page knows about. Mirrors the backend's own list. */
export const INTEGRATION_PROVIDERS = ['usf_sport', 'hls_streams', 'viastoria'] as const;
export type IntegrationProvider = (typeof INTEGRATION_PROVIDERS)[number];

/**
 * `viastoria` is shown as "League Match Stream" and is deliberately absent from every save method
 * below: an administrator configures it, the server refuses a write from here, and the page renders
 * its card read-only. It still reaches `listStreams()` like any other provider.
 */
export type ViastoriaTeam = { tenant: string; teamId: number; slug?: string };

/** One stream the user saved by hand, as edited on the integrations page. */
export type SavedHlsStream = { id?: string; name: string; url: string };

/**
 * Reads and writes the user's integrations, and lists the streams they can connect to.
 *
 * Nothing here is persisted locally: credentials live only on the server, and the stream list is
 * short-lived by nature — a match that was live two minutes ago may be over. The small cache exists
 * so opening the picker twice in a row does not re-hit the vendor's API, not to survive a restart.
 */
@Injectable({
  providedIn: 'root',
})
export class IntegrationsService {
  /** Long enough to cover reopening the picker, short enough that a camera coming online shows up. */
  private static readonly STREAMS_CACHE_MS = 30_000;

  private core?: CoreProvider;

  private integrationsSubject = new BehaviorSubject<IntegrationCredentialModelDto[]>([]);
  get integrations$(): Observable<IntegrationCredentialModelDto[]> {
    return this.integrationsSubject.asObservable();
  }

  private cachedStreams?: { fetchedAt: number; value: StreamListModelDto };

  async initCore(core: CoreProvider) {
    this.core = core;
  }

  async getIntegrations(): Promise<IntegrationCredentialModelDto[]> {
    const integrations = await firstValueFrom(this.core!.api.integrations.integrationsControllerGetIntegrations());
    this.integrationsSubject.next(integrations);
    return integrations;
  }

  find(provider: IntegrationProvider): IntegrationCredentialModelDto | undefined {
    return this.integrationsSubject.value.find((integration) => integration.provider === provider);
  }

  /**
   * Saves usf.sport credentials. An omitted password means "keep the stored one", so the page never
   * has to hold a secret it was never given.
   */
  async saveUsfSport(email: string, password?: string): Promise<IntegrationCredentialModelDto> {
    return this.upsert('usf_sport', { email, ...(password ? { password } : {}) });
  }

  async saveHlsStreams(streams: SavedHlsStream[]): Promise<IntegrationCredentialModelDto> {
    return this.upsert('hls_streams', { streams });
  }

  private async upsert(provider: 'usf_sport' | 'hls_streams', credentials: Record<string, unknown>): Promise<IntegrationCredentialModelDto> {
    const saved = await firstValueFrom(
      this.core!.api.integrations.integrationsControllerUpsertIntegration({ provider, body: { credentials } }),
    );
    this.invalidateStreams();
    await this.getIntegrations();
    return saved;
  }

  async testIntegration(provider: 'usf_sport' | 'hls_streams'): Promise<IntegrationTestResultDto> {
    const result = await firstValueFrom(this.core!.api.integrations.integrationsControllerTestIntegration({ provider }));
    // The check stores the clubs it discovered, so the list has to be re-read to show them.
    await this.getIntegrations();
    return result;
  }

  async deleteIntegration(provider: 'usf_sport' | 'hls_streams'): Promise<void> {
    await firstValueFrom(this.core!.api.integrations.integrationsControllerDeleteIntegration({ provider }));
    this.invalidateStreams();
    await this.getIntegrations();
  }

  /**
   * Streams ready to connect to right now.
   *
   * Returns whatever resolved even when a provider failed — `errors` carries the rest — because the
   * caller is usually about to start a game and a partial list beats none.
   */
  async listStreams(options: { force?: boolean } = {}): Promise<StreamListModelDto> {
    const cached = this.cachedStreams;
    if (!options.force && cached && Date.now() - cached.fetchedAt < IntegrationsService.STREAMS_CACHE_MS) return cached.value;

    const value = await firstValueFrom(this.core!.api.integrations.integrationsControllerGetStreams());
    this.cachedStreams = { fetchedAt: Date.now(), value };
    return value;
  }

  invalidateStreams(): void {
    this.cachedStreams = undefined;
  }

  /** Human-readable label for a stream row: the match/stream name plus where and when it plays. */
  describeStream(stream: StreamOptionModelDto): string {
    const parts = [stream.fieldName, stream.cameraName].filter(Boolean);
    if (stream.startsAt) {
      const startsAt = new Date(stream.startsAt);
      if (!isNaN(startsAt.getTime())) parts.push(startsAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
    }
    return parts.join(' · ');
  }
}
